import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Code2, Plug, MessageSquareCode, ArrowRight, CheckCircle } from 'lucide-react'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { Button } from '@/components/ui/Button'
import { scrollToSection } from '@/lib/utils'

const services = [
  {
    id: 'webapp',
    icon: Code2,
    highlight: true,
  },
  {
    id: 'api',
    icon: Plug,
    highlight: false,
  },
  {
    id: 'consulting',
    icon: MessageSquareCode,
    highlight: false,
  },
]

export function Services() {
  const { t } = useTranslation()
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-32 relative overflow-hidden"
      aria-labelledby="services-heading"
    >
      {/* Background decoration - smaller on mobile */}
      <div className="absolute top-0 right-0 w-48 sm:w-96 h-48 sm:h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-36 sm:w-72 h-36 sm:h-72 bg-secondary/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-5 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-10 sm:mb-16">
            <span className="text-primary font-medium text-base sm:text-lg">{t('services.subtitle')}</span>
            <h2
              id="services-heading"
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 mb-4 sm:mb-6"
            >
              {t('services.title')}
            </h2>
            <p className="text-foreground-secondary text-base sm:text-lg max-w-2xl mx-auto leading-relaxed px-2">
              {t('services.description')}
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8 max-w-6xl mx-auto mb-10 sm:mb-12">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                variants={itemVariants}
                custom={index}
                className="relative"
              >
                {service.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-4 py-1.5 bg-gradient-to-r from-primary to-secondary text-white text-xs font-bold rounded-full">
                      {t('services.popular')}
                    </span>
                  </div>
                )}
                <div
                  className={`bg-background rounded-2xl p-5 sm:p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 h-full border-2 overflow-hidden ${
                    service.highlight ? 'border-primary' : 'border-transparent hover:border-primary/30'
                  }`}
                >
                  <div
                    className={`p-3 sm:p-4 rounded-xl w-fit mb-4 sm:mb-6 transition-transform duration-200 hover:scale-110 ${
                      service.highlight
                        ? 'bg-gradient-to-br from-primary to-secondary text-white'
                        : 'bg-primary/10 text-primary'
                    }`}
                  >
                    <service.icon className="w-6 h-6 sm:w-8 sm:h-8" aria-hidden="true" />
                  </div>

                  <h3 className="text-lg sm:text-xl font-bold mb-1 break-words hyphens-auto">
                    {t(`services.${service.id}.title`)}
                  </h3>

                  <p className="text-primary font-semibold text-sm sm:text-base mb-3 sm:mb-4">
                    {t(`services.${service.id}.price`)}
                  </p>

                  <p className="text-foreground-secondary text-sm sm:text-base mb-5 sm:mb-6 leading-relaxed break-words">
                    {t(`services.${service.id}.description`)}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-2.5 sm:space-y-3 mb-5 sm:mb-6">
                    {(t(`services.${service.id}.features`, { returnObjects: true }) as string[]).map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2.5 text-sm sm:text-base text-foreground-secondary"
                      >
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success flex-shrink-0" aria-hidden="true" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    variant={service.highlight ? 'primary' : 'outline'}
                    className="w-full group"
                    onClick={() => scrollToSection('contact')}
                  >
                    {t('services.inquire')}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div
            variants={itemVariants}
            className="text-center bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 rounded-2xl p-5 sm:p-8 max-w-3xl mx-auto"
          >
            <h3 className="text-lg sm:text-xl font-bold mb-2">{t('services.unsure')}</h3>
            <p className="text-foreground-secondary text-sm sm:text-base mb-4 leading-relaxed">
              {t('services.unsureDescription')}
            </p>
            <Button
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="shadow-lg shadow-primary/25 group w-full sm:w-auto"
            >
              {t('services.freeConsultation')}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
